import axios, { AxiosRequestConfig } from "axios";
import type { APIResult, LINEAuthorizationResponse } from "../my-type";
import {
  AuthService as GeneratedAuthService,
  type PasswordLoginRequest,
  type PasswordAccountCreateRequest,
  type PasswordChangeRequest,
  callApi,
  configureApiClient,
} from "../api/client";

const asApiResult = (value: unknown, fallbackMessage: string): APIResult => {
  if (value && typeof value === "object") {
    const response = value as { statusCode?: number; message?: string; data?: unknown };
    if (typeof response.statusCode === "number") {
      return {
        statusCode: response.statusCode,
        message: response.message ?? fallbackMessage,
        data: response.data,
      };
    }
  }

  return {
    statusCode: 1,
    message: "success",
    data: value,
  };
};

export const bindLineAccount = async (idToken: string, teamCode: string) => {
  try {
    configureApiClient();
    const data = await callApi(
      () => GeneratedAuthService.getApiAuthBindLineAccount(idToken, teamCode),
      "綁定 LINE 帳號失敗"
    );
    return data;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getLINEAccessToken = async (
  code: string,
  redirect_uri: string,
  client_id: string,
  client_secret: string
) => {
  console.log(code, redirect_uri, client_id, client_secret);

  try {
    const grant_type = "authorization_code";
    const axiosConfig: AxiosRequestConfig = {
      method: "POST",
      url: "https://api.line.me/oauth2/v2.1/token",
      data: { grant_type, code, redirect_uri, client_id, client_secret },
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
    };

    console.log(axiosConfig);

    const { data } = await axios(axiosConfig);

    const authResponse: LINEAuthorizationResponse = data;
    return authResponse;
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const checkLINEAccessTokenValidity = async (access_token: string) => {
  try {
    const axiosConfig: AxiosRequestConfig = {
      method: "GET",
      url: "https://api.line.me/oauth2/v2.1/verify",
      params: { access_token },
      headers: {
        "Content-Type": "application/json",
      },
    };

    const { data } = await axios(axiosConfig);

    console.log(data);

    return true;
  } catch (error) {
    console.log(error);
    return false;
  }
};

export interface PasswordLoginPayload {
  teamCode: string;
  username: string;
  password: string;
}

export const loginWithPassword = async (
  payload: PasswordLoginPayload
): Promise<APIResult | null> => {
  try {
    configureApiClient();
    const request: PasswordLoginRequest = {
      teamCode: payload.teamCode,
      username: payload.username,
      password: payload.password,
    };

    const data = await callApi(
      () => GeneratedAuthService.postApiAuthPasswordLogin(request),
      "登入失敗"
    );
    return asApiResult(data, "登入失敗");
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const getCurrentUserProfile = async (token: string): Promise<APIResult | null> => {
  try {
    configureApiClient({
      tokenProvider: () => token,
    });

    const data = await callApi(
      () => GeneratedAuthService.getApiAuthMe(),
      "無法取得使用者資訊"
    );

    return asApiResult(data, "無法取得使用者資訊");
  } catch (error) {
    console.log(error);
    return null;
  }
};

export interface RegisterAccountPayload {
  teamCode: string;
  username: string;
  password: string;
  displayName: string;
  email?: string;
}

export const registerAccount = async (
  payload: RegisterAccountPayload
): Promise<APIResult | null> => {
  try {
    configureApiClient();
    const request: PasswordAccountCreateRequest = {
      teamCode: payload.teamCode,
      username: payload.username,
      password: payload.password,
      displayName: payload.displayName,
      email: payload.email,
    };

    const data = await callApi(
      () => GeneratedAuthService.postApiAuthAccount(request),
      "註冊失敗"
    );
    return asApiResult(data, "註冊失敗");
  } catch (error) {
    console.log(error);
    return null;
  }
};

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export const changePassword = async (
  payload: ChangePasswordPayload,
  token: string
): Promise<APIResult | null> => {
  try {
    configureApiClient({
      tokenProvider: () => token,
    });

    const request: PasswordChangeRequest = {
      currentPassword: payload.currentPassword,
      newPassword: payload.newPassword,
    };

    const data = await callApi(
      () => GeneratedAuthService.postApiAuthAccountChangePassword(request),
      "變更密碼失敗"
    );

    return asApiResult(data, "變更密碼失敗");
  } catch (error) {
    console.log(error);
    return null;
  }
};

export const deleteAccount = async (token: string): Promise<APIResult | null> => {
  try {
    configureApiClient({
      tokenProvider: () => token,
    });

    const data = await callApi(
      () => GeneratedAuthService.deleteApiAuthMe(),
      "刪除帳號失敗"
    );

    return asApiResult(data, "刪除帳號失敗");
  } catch (error) {
    console.log(error);
    return null;
  }
};
